import { useEffect, useState } from 'react';

import { syncStore } from '../stores/sync-store.js';

/**
 * Phase 10 — `useNetworkStatus` and a background `/health` probe.
 *
 * `navigator.onLine` lies frequently (corp Wi-Fi captive portals, VPN
 * suspends, etc.) so we follow up with a cheap HEAD/GET to the API
 * `/health` endpoint every 30 s while we *think* we're offline, and
 * once on every `online` event.
 */

let _probing = false;

const PROBE_PATH = '/api/v1/health';
const POLL_MS = 30_000;

async function probeOnce(): Promise<boolean> {
  if (typeof fetch === 'undefined') return true;
  try {
    const res = await fetch(PROBE_PATH, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      signal: AbortSignal.timeout(4000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function startNetworkMonitor(): () => void {
  if (typeof window === 'undefined') return () => {};
  const update = (online: boolean) => {
    syncStore.getState().setOnline(online);
  };
  const onOnline = async () => {
    update(true);
    const ok = await probeOnce();
    update(ok);
  };
  const onOffline = () => update(false);

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  update(navigator.onLine);

  // Poll only while we think we're offline. We don't want background
  // requests every 30 s in the happy path.
  const interval = window.setInterval(async () => {
    if (_probing) return;
    if (navigator.onLine && syncStore.getState().isOnline) return;
    _probing = true;
    try {
      const ok = await probeOnce();
      update(ok);
    } finally {
      _probing = false;
    }
  }, POLL_MS);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    window.clearInterval(interval);
  };
}

/**
 * Hook returning `{ isOnline }`. Subscribes to the global sync store
 * so the value is consistent everywhere in the tree.
 */
export function useNetworkStatus(): { isOnline: boolean } {
  const [isOnline, setIsOnline] = useState(syncStore.getState().isOnline);
  useEffect(() => {
    return syncStore.subscribe((state) => setIsOnline(state.isOnline));
  }, []);
  return { isOnline };
}
